import { JSX, useCallback, useMemo } from 'react';

import {
	ClickableStepButton,
	ErrorStepIconWrapper,
	InactiveStepIconWrapper,
	StepIconContainer,
	StepLabelText,
	StepWrapper,
	StyledCircleCheck,
	StyledWarningOutline,
	VisitedStepIconWrapper,
} from './styles';
import { StepProps, StepStatus } from './types';

export function getStepStatus(index: number, currentStep: number): StepStatus {
	if (index < currentStep) {
		return StepStatus.COMPLETED;
	}
	if (index === currentStep) {
		return StepStatus.CURRENT;
	}
	return StepStatus.DISABLED;
}

function CustomStepIcon({
	index,
	currentStep,
	hasError,
}: {
	index: number;
	currentStep: number;
	hasError?: boolean;
}): JSX.Element {
	if (hasError) {
		return (
			<ErrorStepIconWrapper data-testid="step-error-icon">
				<StyledWarningOutline />
			</ErrorStepIconWrapper>
		);
	}

	if (index > currentStep) {
		return (
			<InactiveStepIconWrapper data-testid="step-inactive-icon">
				<svg width="8" height="8" viewBox="0 0 16 16">
					<circle cx="8" cy="8" r="8" fill="rgba(0,0,0,0.26)" />
				</svg>
			</InactiveStepIconWrapper>
		);
	}

	if (index < currentStep) {
		return (
			<VisitedStepIconWrapper data-testid="step-completed-icon">
				<StyledCircleCheck />
			</VisitedStepIconWrapper>
		);
	}

	return <VisitedStepIconWrapper>{index + 1}</VisitedStepIconWrapper>;
}

export function Step({
	index,
	currentStep,
	stepKey,
	name,
	hasError,
	onStepClick,
	isLast = false,
}: StepProps): JSX.Element {
	const stepStatus = useMemo(() => getStepStatus(index, currentStep), [currentStep, index]);

	const isActive = index === currentStep;
	const isClickable = index < currentStep;

	const handleClick = useCallback(() => {
		onStepClick?.(stepKey);
	}, [onStepClick, stepKey]);

	const icon = <CustomStepIcon index={index} currentStep={currentStep} hasError={hasError} />;

	return (
		<StepWrapper $isLast={isLast} aria-current={isActive ? 'step' : undefined} data-testid={`STEP_${index}`}>
			{isClickable ? (
				<ClickableStepButton onClick={handleClick} aria-label={`Step to ${name}`}>
					<StepIconContainer>{icon}</StepIconContainer>
					<StepLabelText $stepStatus={stepStatus}>{name}</StepLabelText>
				</ClickableStepButton>
			) : (
				<>
					<StepIconContainer>{icon}</StepIconContainer>
					<StepLabelText $stepStatus={stepStatus}>{name}</StepLabelText>
				</>
			)}
		</StepWrapper>
	);
}

export default Step;
